// For the "Multiple Values" text filter option, users can paste values separated by
// newlines/spaces/commas (blank lines are OK). We treat them as OR tokens.
const splitMultiValuesInput = (raw) => {
  if (raw === null || raw === undefined) return [];
  const s = String(raw).trim();
  if (!s) return [];
  // Split on any whitespace (incl. newlines/tabs) or common separators.
  return s
    .split(/[\s,;]+/g)
    .map(v => v.trim())
    .filter(Boolean);
};

const normalizeMultiValueToken = (val) => String(val ?? '').trim().toLowerCase();

export const multiValuesFilterOption = {
  displayKey: 'multipleValues',
  displayName: 'Multiple Values',
  numberOfInputs: 1,
  predicate: (filterValues, cellValue) => {
    const raw = Array.isArray(filterValues) ? filterValues[0] : filterValues;
    const tokens = splitMultiValuesInput(raw).map(normalizeMultiValueToken).filter(Boolean);
    if (tokens.length === 0) return true; // behave like "no filter"

    if (cellValue === null || cellValue === undefined) return false;
    const cellStr = String(cellValue).trim();
    if (!cellStr) return false;

    // Exact match (prevents CL1 matching ECL1).
    // If cell contains multiple tokens (e.g. "CL1 CL2"), match any token exactly.
    const cellTokens = splitMultiValuesInput(cellStr).map(normalizeMultiValueToken).filter(Boolean);
    if (cellTokens.length > 1) {
      return cellTokens.some(ct => tokens.includes(ct));
    }
    const cellNorm = normalizeMultiValueToken(cellStr);
    return tokens.includes(cellNorm);
  },
};

// Keep AG Grid's existing filter model, persistence and exact-match predicate.
export function bindMultiValueFilters(api, element) {
  function convert(filter, params) {
    if (!filter) return false;
    let changed = false;
    if (filter.filterType === 'text'
        && ['contains', 'equals', 'startsWith'].includes(filter.type)
        && /[;\r\n\t]/.test(filter.filter || '')
        && params?.filterOptions?.some(option => option.displayKey === 'multipleValues')) {
      filter.type = 'multipleValues';
      changed = true;
    }
    (filter.filterModels || []).forEach((child, index) => {
      changed = convert(child, params?.filters?.[index]?.filterParams) || changed;
    });
    (filter.conditions || []).forEach(child => { changed = convert(child, params) || changed; });
    return changed;
  }
  function onFilterChanged() {
    const model = structuredClone(api.getFilterModel());
    let changed = false;
    Object.entries(model).forEach(([id, filter]) => {
      const params = api.getColumn(id)?.getColDef().filterParams;
      changed = convert(filter, params) || changed;
    });
    if (changed) api.setFilterModel(model);
  }
  element.addEventListener('paste', pasteMultiValues);
  api.addEventListener('filterChanged', onFilterChanged);
  api.addEventListener('gridPreDestroyed', () => {
    element.removeEventListener('paste', pasteMultiValues);
    api.removeEventListener('filterChanged', onFilterChanged);
  });
}

export function pasteMultiValues(event) {
  if (event.defaultPrevented) return;
  const input = event.target;
  if (!input.matches('input[type="text"]')
      || !input.closest('.ag-simple-filter-body-wrapper, .ag-floating-filter')) return;
  const text = event.clipboardData.getData('text/plain');
  if (!/[\r\n\t]/.test(text)) return;
  // Text inputs strip newlines before AG Grid can read an Excel column.
  event.preventDefault();
  input.setRangeText(text.trim().replace(/[\r\n\t]+/g, ';'),
    input.selectionStart, input.selectionEnd, 'end');
  input.dispatchEvent(new Event('input', { bubbles: true }));
}
